import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { detectDeviceType } from "./device";

const AuthCtx = createContext(null);
const LEGACY_TOKEN_KEY = "vs_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: `/api/auth/me` reads the HTTP-only auth cookie (or the
  // legacy Authorization header the api client attaches when a pre-cookie
  // localStorage token is still around). If neither is valid we stay
  // logged-out — cookies from XSS-inaccessible storage are now the source
  // of truth.
  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
      })
      .finally(() => setLoading(false));
  }, []);

  // After successful OTP verify the backend already planted the HTTP-only
  // cookie on the response, so we just need to remember the user in memory.
  // We deliberately no longer stash the JWT in localStorage — XSS payloads
  // can no longer hijack the session.
  const login = React.useCallback((_token, u) => {
    // Purge any stale legacy token so we can never accidentally fall back to it.
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
    setUser(u);
  }, []);

  const logout = React.useCallback(async (reason) => {
    // Await the server-side cookie clear before navigating so we don't
    // race the SPA redirect and abort the Set-Cookie response (which
    // showed up as an alarming ERR_ABORTED in devtools). Cookie clearing
    // still happens on the browser side even if the network hop fails,
    // so failures are swallowed silently.
    try { await api.post("/auth/logout"); } catch { /* best effort */ }
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
    setUser(null);
    if (reason) {
      try { sessionStorage.setItem("vs_logout_reason", reason); }
      catch (err) { console.debug("sessionStorage unavailable:", err); }
    }
    window.location.href = "/login";
  }, []);

  // ---- Device-aware inactivity auto-logout ----
  // Desktop / Computer: strictly 30 minutes.
  // Tablet / Mobile: 180 minutes (tap-first UX; browsers throttle timers so we
  // also verify on visibilitychange + focus).
  useEffect(() => {
    if (!user) return undefined;
    const dt = detectDeviceType();
    const TIMEOUT_MS = dt === "desktop" ? 30 * 60 * 1000 : 180 * 60 * 1000;
    let timer = null;
    let lastActivity = Date.now();

    const check = () => {
      if (Date.now() - lastActivity >= TIMEOUT_MS) logout("session_expired");
    };
    const arm = () => {
      lastActivity = Date.now();
      if (timer) clearTimeout(timer);
      timer = setTimeout(check, TIMEOUT_MS);
    };
    const onVisibility = () => {
      // Resume-time check: if the phone was backgrounded past the limit,
      // log out immediately even if the JS timer was throttled.
      if (document.visibilityState === "visible") check();
    };
    const events = ["mousemove", "keydown", "keypress", "click",
                    "touchstart", "touchmove", "scroll", "wheel", "pointerdown"];
    events.forEach(e => window.addEventListener(e, arm, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    arm();
    return () => {
      events.forEach(e => window.removeEventListener(e, arm));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      if (timer) clearTimeout(timer);
    };
  }, [user, logout]);

  // Rolling session refresh: on window focus we ping /api/auth/refresh so
  // the cookie's 30-day expiry gets extended for active users. Errors are
  // ignored — if the session has died the next real API call will 401 and
  // the inactivity logout above will kick in.
  useEffect(() => {
    if (!user) return undefined;
    const refresh = () => api.post("/auth/refresh").catch(() => { /* silent */ });
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user]);

  const can = React.useCallback((perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return (user.permissions || []).includes(perm);
  }, [user]);

  // Memoise the context value so consumers don't re-render every time the
  // provider re-renders for an unrelated reason.
  const ctxValue = useMemo(
    () => ({ user, loading, login, logout, can, setUser }),
    [user, loading, login, logout, can]
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
